import { FlashList } from '@shopify/flash-list';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Animated, TouchableOpacity } from 'react-native';

import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import Highlighter from '@components/HighlightWords';
import { ContactType } from '@components/Invite/Contact';
import { Box, ShadowBox, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { FilterSectionHeader } from '@components/shared/FilterSectionHeader';
import Icon from '@components/shared/Icon/Icon';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';
import {
  Project,
  ProjectSortOption,
  SearchableResult,
  useListContactUsersLazyQuery,
  useSearchLazyQuery,
} from '@graphql/generated';
import { useContacts } from '@hooks/useContacts';
import { useDateRangeString } from '@hooks/useDateRangeString';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import { useListProjectsFromQuery } from '@hooks/useListProjectsFromQuery';
import useMe from '@hooks/useMe';
import { Search } from '@root/types';
import theme from '@themes/theme';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';
import { isProjectSearchResult } from '@utils/typeGuards';

const ESTIMATED_ITEM_SIZE = 36;
const PROJECT_SECTION_MAX_HEIGHT = 36 * 8 + 8;
const USER_SECTION_MAX_HEIGHT = 36 * 5 + 8;
export const PANEL_WIDTH = 380;
const MIN_SEARCH_LENGTH = 3;

export type PopupActivityFilterPanelProps = {
  showProjects?: boolean;
  initialUserOpen?: boolean;
  selectedProjects: Project[];
  onSelectProject: (projects: Project[]) => void;
  selectedUsers: ContactType[];
  onSelectUser: (users: ContactType[]) => void;
  selectedDueDateRange?: DateRange;
  onPressSelectDates: () => void;
  onClearAll: () => void;
};

export const PopupActivityFilterPanel: React.FC<
  PopupActivityFilterPanelProps
> = ({
  showProjects = true,
  initialUserOpen,
  selectedProjects,
  onSelectProject,
  selectedUsers,
  onSelectUser,
  selectedDueDateRange,
  onPressSelectDates,
  onClearAll,
}) => {
  const { projects, fetchFromCursor } = useListProjectsFromQuery({
    first: 25,
    sortBy: ProjectSortOption.NameAsc,
    archivesOnly: false,
  });

  const { contacts } = useContacts();
  const { me } = useMe();

  const contactsAndMe =
    !me || contacts.some((c) => c.id === me.id) ? contacts : [...contacts, me];
  const userList = convertUsersToContacts(contactsAndMe).sort((a, b) =>
    (a.firstName ?? '').localeCompare(b.firstName ?? '')
  );

  const selectedDateRangeStr = useDateRangeString({
    selectedDueDateRange,
    useTranslationFormatter: true,
  });

  const [search, setSearch] = useState('');
  const [hoveredProjectId, setHoveredProjectId] = useState('');
  const [hoveredUserId, setHoveredUserId] = useState('');

  // === Project search (server-side, with pagination) ===
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [globalSearch] = useSearchLazyQuery();
  const [projectSearchResults, setProjectSearchResults] = useState<Project[]>(
    []
  );
  const [projectSearchFrom, setProjectSearchFrom] = useState(0);
  const [projectSearchTotal, setProjectSearchTotal] = useState(0);

  const doProjectSearch = useCallback(
    async (term: string, from: number) => {
      try {
        const queryResult = await globalSearch({
          variables: {
            term,
            size: 20,
            from,
            includeMessages: false,
            includeDocuments: false,
            includeProjects: true,
            includeTasks: false,
          },
        });
        const data =
          (queryResult.data?.search as Search<SearchableResult>[]) || [];
        const newProjects = data
          .filter(isProjectSearchResult)
          .map((r) => r.record as Project);
        if (from === 0) {
          setProjectSearchResults(newProjects);
        } else {
          setProjectSearchResults((prev) => {
            const ids = new Set(prev.map((p) => p.id));
            return [...prev, ...newProjects.filter((p) => !ids.has(p.id))];
          });
        }
        setProjectSearchTotal(data[0]?.total ?? 0);
        setProjectSearchFrom(from + newProjects.length);
        setLoadingProjects(false);
      } catch {
        setLoadingProjects(false);
      }
    },
    [globalSearch]
  );

  // Debounce timing/spinner only migrated to the shared hook (300ms, via
  // isPending below) — the below-3-chars fallback-to-full-list behavior is a
  // documented exception and stays driven by the local MIN_SEARCH_LENGTH
  // checks throughout this file, not the hook's isBelowThreshold (TA-10168).
  const handleProjectSearch = useCallback(
    (term: string) => {
      setLoadingProjects(true);
      setProjectSearchResults([]);
      setProjectSearchFrom(0);
      setProjectSearchTotal(0);
      return doProjectSearch(term, 0);
    },
    [doProjectSearch]
  );

  const {
    isPending: isProjectSearchPending,
    hasSearched: hasSearchedProjects,
  } = useDebouncedSearch(search, handleProjectSearch, {
    minChars: MIN_SEARCH_LENGTH,
  });
  // The trailing `!hasSearchedProjects` closes the single-render gap right
  // after the debounce settles but before handleProjectSearch's effect has
  // actually fired — without it, isProjectSearchLoading can briefly go false
  // while filteredProjects is still [], flashing "No results found". Gated
  // on the length check too (not just OR'd in) since hasSearchedProjects is
  // also false for the entire below-threshold regime, where this must stay
  // false so an empty local project list still shows "No results found"
  // instead of a permanent spinner (TA-10168).
  const isProjectSearchLoading =
    loadingProjects ||
    isProjectSearchPending ||
    (search.trim().length >= MIN_SEARCH_LENGTH && !hasSearchedProjects);

  // Gated on hasSearchedProjects (not just the threshold) so a still-
  // debouncing new term can't keep showing the OLD term's stale
  // projectSearchResults — hasSearchedProjects resets to false the instant
  // the new term starts debouncing, so this renders empty (showing the
  // pending spinner instead) until the new term's search actually fires
  // (TA-10168).
  const filteredProjects = useMemo(() => {
    if (search.trim().length < MIN_SEARCH_LENGTH) return projects;
    return hasSearchedProjects ? projectSearchResults : [];
  }, [search, projectSearchResults, projects, hasSearchedProjects]);

  // hasSearchedProjects is required in addition to the from/total bounds —
  // it resets to false the instant a new term starts debouncing, before
  // handleProjectSearch resets projectSearchFrom/projectSearchTotal. Without
  // it, load-more could fire doProjectSearch with the NEW term but the OLD
  // term's offset/total still in state, mixing/skipping results (TA-10168).
  const canLoadMoreProjects =
    search.trim().length >= MIN_SEARCH_LENGTH &&
    hasSearchedProjects &&
    projectSearchFrom > 0 &&
    projectSearchFrom < projectSearchTotal;

  const getProjectListOnEndReached = () => {
    if (search.trim().length < MIN_SEARCH_LENGTH) return fetchFromCursor;
    if (canLoadMoreProjects)
      // Trim to match the term useDebouncedSearch already sent for page 0 —
      // using the raw (untrimmed) search state here could send a different
      // term than page 0 for input with leading/trailing whitespace,
      // breaking pagination consistency.
      return () => doProjectSearch(search.trim(), projectSearchFrom);
    return undefined;
  };
  const projectListOnEndReached = getProjectListOnEndReached();

  // === User search (server-side) ===
  const [searchUsers, { data: searchUsersData, loading: loadingUsers }] =
    useListContactUsersLazyQuery();

  const handleUserSearch = useCallback(
    (term: string) => searchUsers({ variables: { term } }),
    [searchUsers]
  );

  const { isPending: isUserSearchPending, hasSearched: hasSearchedUsers } =
    useDebouncedSearch(search, handleUserSearch, {
      minChars: MIN_SEARCH_LENGTH,
    });
  // See isProjectSearchLoading above for why the trailing hasSearchedUsers
  // check is gated on the length check rather than OR'd in directly
  // (TA-10168).
  const isUserSearchLoading =
    loadingUsers ||
    isUserSearchPending ||
    (search.trim().length >= MIN_SEARCH_LENGTH && !hasSearchedUsers);

  const filteredUsers = useMemo(() => {
    if (search.trim().length < MIN_SEARCH_LENGTH) return userList;
    if (searchUsersData?.listContactUsers) {
      const term = search.trim().toLowerCase();
      const showMe =
        !me ||
        me.name?.toLowerCase().includes(term) ||
        me.firstName?.toLowerCase().includes(term) ||
        me.lastName?.toLowerCase().includes(term) ||
        me.email?.toLowerCase().includes(term);
      const users =
        me &&
        showMe &&
        !searchUsersData.listContactUsers.some((user) => user.id === me.id)
          ? [...searchUsersData.listContactUsers, me]
          : searchUsersData.listContactUsers;

      return convertUsersToContacts(users);
    }
    return [];
  }, [search, me, searchUsersData, userList]);

  // === Accordion open/close state ===
  const [isProjectOpen, setIsProjectOpen] = useState(showProjects);
  const [isUserOpen, setIsUserOpen] = useState(
    initialUserOpen ?? !showProjects
  );

  const projectMaxHeight = Math.max(
    44,
    Math.min(filteredProjects.length * 36 + 8, PROJECT_SECTION_MAX_HEIGHT)
  );
  const userMaxHeight = Math.max(
    44,
    Math.min(filteredUsers.length * 36 + 8, USER_SECTION_MAX_HEIGHT)
  );

  const projectAnim = useRef(
    new Animated.Value(isProjectOpen ? PROJECT_SECTION_MAX_HEIGHT : 0)
  ).current;
  const projectIconAnim = useRef(
    new Animated.Value(isProjectOpen ? 1 : 0)
  ).current;

  const userAnim = useRef(
    new Animated.Value(isUserOpen ? USER_SECTION_MAX_HEIGHT : 0)
  ).current;
  const userIconAnim = useRef(new Animated.Value(isUserOpen ? 1 : 0)).current;

  // Update project section height when data/filter changes
  useEffect(() => {
    if (isProjectOpen) {
      Animated.timing(projectAnim, {
        toValue: projectMaxHeight,
        duration: 200,
        useNativeDriver: false,
      }).start();
    }
  }, [projectMaxHeight, isProjectOpen, projectAnim]);

  // Update user section height when data/filter changes
  useEffect(() => {
    if (isUserOpen) {
      Animated.timing(userAnim, {
        toValue: userMaxHeight,
        duration: 200,
        useNativeDriver: false,
      }).start();
    }
  }, [userMaxHeight, isUserOpen, userAnim]);

  // Skip on initial mount so default open/closed state is preserved
  const isSearchEffectFirstRun = useRef(true);

  // Auto-expand sections when searching
  useEffect(() => {
    if (isSearchEffectFirstRun.current) {
      isSearchEffectFirstRun.current = false;
      return;
    }
    if (search.length > 0) {
      if (!isProjectOpen) {
        setIsProjectOpen(true);
        Animated.timing(projectAnim, {
          toValue: projectMaxHeight,
          duration: 200,
          useNativeDriver: false,
        }).start();
        Animated.timing(projectIconAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }).start();
      }
      if (showProjects) {
        if (!isUserOpen) {
          setIsUserOpen(true);
          Animated.timing(userAnim, {
            toValue: userMaxHeight,
            duration: 200,
            useNativeDriver: false,
          }).start();
          Animated.timing(userIconAnim, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
          }).start();
        }
      }
    } else if (showProjects && search.trim().length === 0 && isUserOpen) {
      setIsUserOpen(false);
      Animated.timing(userAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: false,
      }).start();
      Animated.timing(userIconAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [search]);

  const animateSection = (
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    heightAnim: Animated.Value,
    maxHeight: number,
    iconAnim: Animated.Value
  ) => {
    setIsOpen((prev) => {
      const next = !prev;
      Animated.timing(heightAnim, {
        toValue: next ? maxHeight : 0,
        duration: 200,
        useNativeDriver: false,
      }).start();
      Animated.timing(iconAnim, {
        toValue: next ? 1 : 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
      return next;
    });
  };

  const toggleProjectSection = () => {
    if (!isProjectOpen && isUserOpen) {
      animateSection(setIsUserOpen, userAnim, userMaxHeight, userIconAnim);
    }
    animateSection(
      setIsProjectOpen,
      projectAnim,
      projectMaxHeight,
      projectIconAnim
    );
  };

  const toggleUserSection = () => {
    if (!isUserOpen && isProjectOpen && showProjects) {
      animateSection(
        setIsProjectOpen,
        projectAnim,
        projectMaxHeight,
        projectIconAnim
      );
    }
    animateSection(setIsUserOpen, userAnim, userMaxHeight, userIconAnim);
  };

  const onPressProject = (project: Project) => {
    const isIn = selectedProjects.some((p) => p.id === project.id);
    onSelectProject(
      isIn
        ? selectedProjects.filter((p) => p.id !== project.id)
        : [...selectedProjects, project]
    );
  };

  const onPressUser = (contact: ContactType) => {
    const isIn = selectedUsers.some((u) => u.id === contact.id);
    onSelectUser(
      isIn
        ? selectedUsers.filter((u) => u.id !== contact.id)
        : [...selectedUsers, contact]
    );
  };

  const hasActiveFilters =
    selectedProjects.length > 0 ||
    selectedUsers.length > 0 ||
    !!(selectedDueDateRange?.start || selectedDueDateRange?.end);

  return (
    <ShadowBox
      flex={1}
      width={PANEL_WIDTH}
      alignItems='center'
      justifyContent='center'
      borderRadius='xs'
      borderWidth={1}
      borderColor='grey02'
      variant='base'
      backgroundColor='white'>
      <>
        {/* ===== Search Input ===== */}
        <Box style={{ width: PANEL_WIDTH }} px='m' pt='s' pb='xs'>
          <SearchInput
            value={search}
            height={40}
            onTextChanged={(value) => setSearch(value)}
            placeHolder='Search'
            onCancel={() => setSearch('')}
          />
        </Box>

        {/* ===== Projects Section ===== */}
        {showProjects && (
          <Box style={{ width: PANEL_WIDTH }}>
            <FilterSectionHeader
              title='Projects'
              badgeNumber={selectedProjects.length}
              onPress={toggleProjectSection}
              iconAnim={projectIconAnim}
              accessibilityLabel='Projects filter section'
            />

            {filteredProjects.length === 0 && isProjectSearchLoading && (
              <Box my='xs'>
                <ActivityIndicatorLoading />
              </Box>
            )}

            <Animated.View
              style={{
                overflow: 'hidden',
                height: projectAnim,
                borderLeftWidth: 1,
                borderRightWidth: 1,
                borderColor: theme.colors.grey02,
              }}>
              <Box pb='xs' flex={1}>
                {!isProjectSearchLoading && filteredProjects.length === 0 ? (
                  <Box
                    mx='m'
                    height={48}
                    alignItems='center'
                    justifyContent='center'>
                    <Text
                      variant='webBodySecondary'
                      color='grey04'
                      textAlign='center'>
                      No results found
                    </Text>
                  </Box>
                ) : (
                  <FlashList
                    extraData={selectedProjects}
                    onEndReached={projectListOnEndReached}
                    showsVerticalScrollIndicator={false}
                    data={filteredProjects}
                    renderItem={({ item: project }) => (
                      <Box
                        height={36}
                        justifyContent='center'
                        accessibilityLabel='Project Item'
                        style={{
                          backgroundColor:
                            hoveredProjectId === project.id
                              ? theme.colors.grey01
                              : theme.colors.white,
                        }}
                        onMouseEnter={() => setHoveredProjectId(project.id)}
                        onMouseLeave={() => setHoveredProjectId('')}>
                        <TouchableOpacity
                          onPress={() => onPressProject(project)}
                          accessibilityLabel={project.name}>
                          <Box
                            flexDirection='row'
                            alignItems='center'
                            marginLeft='m'
                            justifyContent='space-between'>
                            <CheckBoxButton
                              checked={selectedProjects.some(
                                (p) => p.id === project.id
                              )}
                            />
                            <Box ml='xs' mr='m' flex={1}>
                              <Text
                                variant='webBodySecondary'
                                color='textSecondary'
                                numberOfLines={1}>
                                <Highlighter
                                  highlightStyle={{
                                    backgroundColor: theme.colors.yellowBright,
                                  }}
                                  autoEscape
                                  searchWords={search?.split(' ') || ''}
                                  textToHighlight={project.name}
                                />
                              </Text>
                            </Box>
                          </Box>
                        </TouchableOpacity>
                      </Box>
                    )}
                    estimatedItemSize={ESTIMATED_ITEM_SIZE}
                  />
                )}
              </Box>
            </Animated.View>
          </Box>
        )}

        {/* ===== User Section ===== */}
        <Box style={{ width: PANEL_WIDTH }}>
          <FilterSectionHeader
            title='User'
            badgeNumber={selectedUsers.length}
            onPress={toggleUserSection}
            iconAnim={userIconAnim}
            accessibilityLabel='User filter section'
          />

          <Animated.View
            style={{
              overflow: 'hidden',
              height: userAnim,
              borderLeftWidth: 1,
              borderRightWidth: 1,
              borderColor: theme.colors.grey02,
            }}>
            <Box pb='xs' flex={1}>
              {search.trim().length >= MIN_SEARCH_LENGTH &&
                isUserSearchLoading && (
                  <Box my='xs'>
                    <ActivityIndicatorLoading />
                  </Box>
                )}
              {!isUserSearchLoading && filteredUsers.length === 0 && (
                <Box
                  mx='m'
                  height={48}
                  alignItems='center'
                  justifyContent='center'>
                  <Text
                    variant='webBodySecondary'
                    color='grey04'
                    textAlign='center'>
                    No results found
                  </Text>
                </Box>
              )}
              {!isUserSearchLoading && filteredUsers.length > 0 && (
                <FlashList
                  extraData={selectedUsers}
                  showsVerticalScrollIndicator={false}
                  data={filteredUsers}
                  renderItem={({ item: contact }) => (
                    <Box
                      height={36}
                      justifyContent='center'
                      accessibilityLabel='User Item'
                      style={{
                        backgroundColor:
                          hoveredUserId === contact.id
                            ? theme.colors.grey01
                            : theme.colors.white,
                      }}
                      onMouseEnter={() => setHoveredUserId(contact.id)}
                      onMouseLeave={() => setHoveredUserId('')}>
                      <TouchableOpacity
                        onPress={() => onPressUser(contact)}
                        accessibilityLabel={contact.displayName}>
                        <Box
                          flexDirection='row'
                          alignItems='center'
                          justifyContent='space-between'>
                          <Box ml='m' justifyContent='center'>
                            <CheckBoxButton
                              checked={selectedUsers.some(
                                (u) => u.id === contact.id
                              )}
                            />
                          </Box>
                          <Box mr='m' marginLeft='xs' flex={1}>
                            <Text
                              variant='webBodySecondary'
                              color={
                                contact.displayName ? 'textSecondary' : 'grey04'
                              }
                              numberOfLines={1}>
                              <Highlighter
                                highlightStyle={{
                                  backgroundColor: theme.colors.yellowBright,
                                }}
                                autoEscape
                                searchWords={search?.split(' ') || ''}
                                textToHighlight={
                                  contact.displayName || 'No name'
                                }
                              />
                            </Text>
                          </Box>
                        </Box>
                      </TouchableOpacity>
                    </Box>
                  )}
                  estimatedItemSize={ESTIMATED_ITEM_SIZE}
                />
              )}
            </Box>
          </Animated.View>
        </Box>

        {/* ===== Select Dates Row ===== */}
        <Box style={{ width: PANEL_WIDTH }}>
          <Box
            borderTopWidth={1}
            borderTopColor='grey02'
            height={48}
            px='m'
            flexDirection='row'
            alignItems='center'>
            <TouchableOpacity
              accessibilityLabel='Select date range'
              onPress={onPressSelectDates}
              style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
              <Icon
                name='Calendar'
                color={selectedDateRangeStr ? 'greenSecondary' : 'grey05'}
                variant='m'
              />
              <Text
                ml='xs'
                variant='labelEmphasized'
                color={
                  selectedDateRangeStr ? 'greenSecondary' : 'textSecondary'
                }
                flex={1}
                numberOfLines={1}>
                {selectedDateRangeStr || 'Select Dates'}
              </Text>
            </TouchableOpacity>
          </Box>
        </Box>

        {/* ===== Clear All Footer ===== */}
        <Box
          width={PANEL_WIDTH}
          borderTopWidth={1}
          borderTopColor='grey02'
          height={48}
          alignItems='center'
          justifyContent='center'>
          <TouchableOpacity
            accessibilityLabel='Clear all filters'
            onPress={onClearAll}>
            <Text
              variant='webBodySecondary'
              color={hasActiveFilters ? 'textPrimary' : 'grey04'}>
              Clear All
            </Text>
          </TouchableOpacity>
        </Box>
      </>
    </ShadowBox>
  );
};
